import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useVersion } from '../contexts/VersionContext';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/localization/localization';
import { logger } from '../utils/logger';
import LocalStorageService from '../utils/localStorageService';
import CompanyAnalyzerService from '../services/companyAnalyzerService';
import { API_BASE_URL } from '../config';

// Custom hooks
import { useVersionManagement } from '../hooks/useVersionManagement';
import { useDataPersistence } from '../hooks/useDataPersistence';
import { useFileHandling } from '../hooks/useFileHandling';
import { useAnalysisOperations } from '../hooks/useAnalysisOperations';
import { useLanguageAwareRender } from '../hooks/useLanguageAwareRender';

// Common UI components
import { LoadingState, ErrorState, EmptyState } from './common';

// Material UI components
import {
  Button,
  Box,
  Paper,
  Typography,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material';

// Material UI icons
import HistoryIcon from '@mui/icons-material/History';
import SaveIcon from '@mui/icons-material/Save';

// File uploader components
import FileUpload from './FileUpload';

// Local components
import VersionHistory from './VersionHistory';

/**
 * Shared analyzer page component with common functionality
 * Used as the base for CV, Job, and Company analysis pages
 */
const AnalyzerPage = ({
  type, // 'cv', 'job', or 'company'
  steps,
  title,
  userId,
  viewOnly = false,
  onComplete,
  onStatusChange,
  renderDataDisplay, // Function to render the specific data display
  renderEditDialog,  // Function to render the edit dialog for this type
  getInitialState,   // Function to get initial state values
  uploadProps,
  onConfirm,         // Function called when user confirms analysis
  _handleEditSkill,   // Function to handle editing a skill (job analyzer) - unused but kept for compatibility
  handleSaveEdit,    // Function to handle saving edits (job analyzer)
  handleRemoveSkill // Keep this for backward compatibility
}) => {
  // Get user context, version functions, and language context
  const { currentUser } = useAuth();
  const _versionContext = useVersion();
  const { currentLanguage } = useLanguage();

  // Use language-aware render hook for consistent language change handling
  const renderKey = useLanguageAwareRender('AnalyzerPage');

  // Custom hooks
  const {
    versions,
    viewingVersion,
    setViewingVersion,
    loadVersions,
    loadVersionData,
    saveVersion,
    restoreVersion,
    getTargetUserId
  } = useVersionManagement(type, userId, currentUser);

  const {
    _saveUserData,
    getUserData,
    _saveItemsWithVersion,
    saveAnalysisResult
  } = useDataPersistence(type);

  const {
    activeStep,
    setActiveStep,
    completedSteps,
    setCompletedSteps,
    loading,
    _setLoading,
    analyzing,
    error,
    setError,
    success,
    setSuccess,
    result,
    setResult,
    _data,
    setData,
    openEditDialog,
    setOpenEditDialog,
    editingItem,
    editingIndex,
    startAnalysis,
    _pollRequestStatus,
    processAnalysisResult,
    handleAnalysisError,
    _moveToNextStep,
    moveToPreviousStep,
    startEditing,
    stopEditing
  } = useAnalysisOperations(type, onComplete, onStatusChange);

  // Simple file processing function to extract text from files
  const processFiles = useCallback(async (files) => {
    logger.info('[ANALYZER PAGE]', 'Processing files for analysis', { filesCount: files.length });

    // For now, just return the files as-is - the backend will handle text extraction
    return {
      files: files,
      text: '', // Backend will extract text from files
      userId: getTargetUserId()
    };
  }, [getTargetUserId]);

  const {
    files,
    _setFiles,
    text,
    _setText,
    _fileDrop,
    onDrop,
    handleTextChange,
    handleRemoveFile,
    prepareFilesForAnalysis
  } = useFileHandling(processFiles, setCompletedSteps);

  // Additional state
  const [openBackConfirmDialog, setOpenBackConfirmDialog] = useState(false);

  // Tab state with localStorage persistence to survive language changes
  const getInitialTabValue = useCallback(() => {
    try {
      const saved = LocalStorageService.getItem(`${type}_analyzer_tab_value`);
      return saved !== null ? parseInt(saved, 10) : 0;
    } catch (error) {
      logger.warn('[ANALYZER PAGE] Error loading tab value from storage:', error);
      return 0;
    }
  }, [type]);

  const [tabValue, setTabValue] = useState(getInitialTabValue);
  const [programmaticTabChange, setProgrammaticTabChange] = useState(false);

  // Custom state for components that need it (like company research for editing)
  const [customState, setCustomState] = useState(() => {
    return getInitialState ? getInitialState() : {};
  });

  // Function to update custom state
  const updateCustomState = useCallback((newState) => {
    setCustomState(newState);
  }, []);

  // This effect is now replaced by the language-specific effect below

  // Helper functions
  const hasValidInput = () => {
    return files.length > 0 || text.trim().length > 0;
  };

  // Load data on mount
  // Consolidated data loading effect to prevent multiple API calls
  useEffect(() => {
    const targetUserId = getTargetUserId();
    if (!targetUserId) return undefined;

    let isCancelled = false; // Prevent state updates if component unmounts

    const loadAllData = async () => {
      logger.info(`[ANALYZER PAGE] Loading all ${type} data for user ${targetUserId}`);

      try {
        let savedData = null;
        let _versionsData = null;

        if (type === 'company') {
          // Company research uses different data loading approach
          logger.info('[ANALYZER PAGE] Loading company research data');

          try {
            // First, try to get the user's company mapping
            const companyMapping = await CompanyAnalyzerService.getUserCompanyMapping(targetUserId);
            logger.info('[ANALYZER PAGE] Company mapping:', companyMapping);

            if (companyMapping && companyMapping.companyName && companyMapping.companyName !== 'Default Company') {
              // Load company data by company name only if we have a real company mapping
              logger.info('[ANALYZER PAGE] Loading company data for mapped company:', companyMapping.companyName);
              savedData = await CompanyAnalyzerService.loadCompanyData(companyMapping.companyName);
              logger.info('[ANALYZER PAGE] Loaded company data from disk:', savedData);
            } else {
              logger.info('[ANALYZER PAGE] No specific company mapping found, checking for available company research data');

              // Try to load any available company research data from disk
              try {
                logger.info('[ANALYZER PAGE] Attempting to load available company research data from default location');

                // Use a different approach - call the /api/company/load endpoint directly
                const response = await fetch(`${API_BASE_URL}/company/load`);
                if (response.ok) {
                  const apiResponse = await response.json();
                  if (apiResponse.result && (apiResponse.result.sections || apiResponse.result.report)) {
                    logger.info('[ANALYZER PAGE] Successfully loaded company research from default location:', apiResponse.result);
                    savedData = apiResponse.result;
                  } else {
                    logger.info('[ANALYZER PAGE] No company research data available in default location, using empty state');
                    savedData = { companyName: '', report: '', sections: [] };
                  }
                } else {
                  logger.info('[ANALYZER PAGE] No company research data found, using empty state');
                  savedData = { companyName: '', report: '', sections: [] };
                }
              } catch (error) {
                logger.warn('[ANALYZER PAGE] Could not load available company research, using empty state:', error);
                savedData = { companyName: '', report: '', sections: [] };
              }
            }
          } catch (error) {
            logger.error('[ANALYZER PAGE] Error loading company data:', error);
          }

          // Load versions for company research
          _versionsData = await loadVersions().catch(error => {
            logger.error('[ANALYZER PAGE] Error loading company versions:', error);
            return null;
          });
        } else {
          // CV and Job types use regular user data loading
          const [userData, versions] = await Promise.all([
            getUserData(type, targetUserId).catch(error => {
              logger.error('[ANALYZER PAGE] Error loading user data:', error);
              return null;
            }),
            loadVersions().catch(error => {
              logger.error('[ANALYZER PAGE] Error loading versions:', error);
              return null;
            })
          ]);

          savedData = userData;
          _versionsData = versions;
        }

        // Only update state if component is still mounted
        if (isCancelled) return undefined;

        logger.info(`[ANALYZER PAGE] Loaded saved ${type} data:`, savedData);

        if (savedData && Object.keys(savedData).length > 0) {
          // Set data and result based on type
          const hasValidData =
            (type === 'cv' && (savedData.competencies || (Array.isArray(savedData) && savedData.length > 0))) ||
            (type === 'job' && (savedData.skills || (Array.isArray(savedData) && savedData.length > 0))) ||
            (type === 'company' && (
              (Array.isArray(savedData.sections) ? savedData.sections.length > 0 : !!savedData.sections) ||
              !!savedData.report ||
              !!savedData.companyName
            ));

          logger.info(`[ANALYZER PAGE] hasValidData check: type=${type}, hasValidData=${hasValidData}`, {
            isArray: Array.isArray(savedData),
            hasCompetencies: !!(savedData.competencies),
            hasSkills: !!(savedData.skills),
            dataKeys: Object.keys(savedData)
          });

          if (hasValidData) {
            // If data is an array, wrap it in the expected structure
            let processedData = savedData;
            if (Array.isArray(savedData)) {
              if (type === 'cv') {
                processedData = { competencies: savedData };
              } else if (type === 'job') {
                processedData = { skills: savedData };
              }
            }

            setResult(processedData);
            setData(processedData);
            setCompletedSteps([0]);
            setActiveStep(1);

            if (onStatusChange) {
              onStatusChange('completed');
            }
          }
        }
      } catch (error) {
        if (!isCancelled) {
          logger.error('[ANALYZER PAGE] Error loading data:', error);
        }
      }
      return undefined;
    };

    loadAllData();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isCancelled = true;
    };
  }, [
    type,
    userId,
    currentUser?.id,
    getUserData,
    getTargetUserId,
    loadVersions,
    onStatusChange,
    setActiveStep,
    setCompletedSteps,
    setData,
    setResult
  ]);

  // Separate effect to handle language changes without reloading data
  useEffect(() => {
    // When language changes, forcefully restore tab state after a brief delay
    const timer = setTimeout(() => {
      // Don't restore tab if this is a programmatic change
      if (programmaticTabChange) {
        logger.info('[ANALYZER PAGE] Skipping tab restore due to programmatic change');
        return;
      }

      const savedTabValue = getInitialTabValue();
      logger.info('[ANALYZER PAGE] Language changed, checking tab state. Current:', tabValue, 'Saved:', savedTabValue);

      if (savedTabValue !== tabValue) {
        logger.info('[ANALYZER PAGE] Restoring saved tab value after language change:', savedTabValue);
        setTabValue(savedTabValue);
      }
    }, 100); // Small delay to ensure all other effects have run

    return () => clearTimeout(timer);
  }, [currentLanguage, tabValue, programmaticTabChange, getInitialTabValue]); // Include programmaticTabChange to detect changes

  // Handle confirm (analyze) button for job/cv analyzer
  const handleConfirm = async () => {
    logger.info(`[ANALYZER PAGE] handleConfirm called for ${type} analyzer`);

    if (!hasValidInput()) {
      setError(t('errors.validationError'));
      return;
    }

    try {
      setError('');

      // Prepare data for analysis
      const analysisData = await prepareFilesForAnalysis();
      if (!analysisData) {
        setError(t('errors.fileUploadError'));
        return;
      }

      // Add userId to analysis data - required for all analysis types
      const targetUserId = getTargetUserId();
      const enrichedAnalysisData = {
        ...analysisData,
        userId: targetUserId
      };

      logger.info(`[ANALYZER PAGE] Analysis data prepared with userId: ${targetUserId}`, enrichedAnalysisData);

      // Start analysis - new services return results directly
      const analysisResult = await startAnalysis(enrichedAnalysisData);
      if (!analysisResult) {
        return; // Error already handled in startAnalysis
      }

      // Process the result immediately (no polling needed)
      processAnalysisResult(analysisResult);

      // Save the result
      await saveAnalysisResult(targetUserId, analysisResult, {
        files: files.map(f => f.name),
        timestamp: new Date().toISOString()
      });

      setSuccess(t('common.saveSuccess'));

    } catch (error) {
      logger.error('[ANALYZER PAGE] Error in handleConfirm:', error);
      handleAnalysisError(error.message || t('errors.genericError'));
    }
  };

  // Handle saving skills/competencies (always creates version on change)
  const handleSaveItems = async () => {
    const targetUserId = getTargetUserId();

    logger.info(`[ANALYZER PAGE] Saving ${type} items for user ${targetUserId}`);

    try {
      // Get items based on type
      const items = type === 'cv' ? result?.competencies : result?.skills;
      if (!items) {
        logger.error('[ANALYZER PAGE] No items to save');
        return;
      }

      // Save full result as version (this will handle both items and result structure)
      // Let the hook determine proper author based on currentUser role
      await saveVersion(result, null, {
        description: `${type === 'cv' ? 'Competencies' : 'Skills'} updated`,
        operation: 'manual_save'
      });

      setSuccess(t('common.saveSuccess'));
    } catch (error) {
      logger.error('[ANALYZER PAGE] Error saving items:', error);
      setError(t('errors.genericError'));
    }
  };

  // Handle saving company research data (creates version on change)
  const handleSaveCompanyData = async (sectionName = 'Section') => {
    const targetUserId = getTargetUserId();

    logger.info(`[ANALYZER PAGE] Saving company research data for user ${targetUserId}`, { sectionName });

    try {
      if (!result) {
        logger.error('[ANALYZER PAGE] No company research data to save');
        return;
      }

      // Save full result as version
      await saveVersion(result, null, {
        description: `Company research updated: ${sectionName} edited`,
        operation: 'section_edit'
      });

      setSuccess(t('common.saveSuccess'));
      logger.info(`[ANALYZER PAGE] Company research saved successfully: ${sectionName}`);
    } catch (error) {
      logger.error('[ANALYZER PAGE] Error saving company research:', error);
      setError(t('errors.genericError'));
    }
  };

  // Handle version selection
  const handleVersionSelect = async (version) => {
    logger.info('[ANALYZER PAGE] Version selected:', version);

    const versionData = await loadVersionData(version);
    if (versionData) {
      setViewingVersion(version);
      setResult(versionData);
      setData(versionData);
      setCompletedSteps([0]);
      setActiveStep(1);

      // Store the desired tab value in localStorage before changing
      try {
        LocalStorageService.setItem(`${type}_analyzer_tab_value`, '0');
        logger.info('[ANALYZER PAGE] Stored tab value 0 in localStorage for version view');
      } catch (error) {
        logger.error('[ANALYZER PAGE] Error storing tab value:', error);
      }

      setProgrammaticTabChange(true); // Flag this as a programmatic change
      setTabValue(0); // Switch to Analysis tab to show the version data
      logger.info('[ANALYZER PAGE] Switched to Analysis tab to display version data');

      // Clear the programmatic flag after a longer delay to allow all language effects to complete
      setTimeout(() => {
        setProgrammaticTabChange(false);
        logger.info('[ANALYZER PAGE] Cleared programmatic tab change flag');
      }, 3000);
    }
  };

  // Handle version restore (creates new version with old data)
  const handleVersionRestore = async (version) => {
    logger.info('[ANALYZER PAGE] Creating new version from:', version);

    try {
      const restoredData = await restoreVersion(version);
      if (restoredData) {
        setResult(restoredData);
        setData(restoredData);
        setViewingVersion(null);
        setSuccess('New version created successfully from selected data');
        logger.info('[ANALYZER PAGE] New version created successfully');
      }
    } catch (error) {
      logger.error('[ANALYZER PAGE] Error creating new version:', error);
      setError(`Failed to create new version: ${error.message}`);
    }
  };

  // Handle back navigation with confirmation
  const handleBack = () => {
    if (activeStep === 1 && result) {
      setOpenBackConfirmDialog(true);
    } else {
      moveToPreviousStep();
    }
  };

  const handleBackConfirm = () => {
    setOpenBackConfirmDialog(false);
    moveToPreviousStep();
    setResult(null);
    setData(getInitialState ? getInitialState() : {});
  };

  // Tab change handler with localStorage persistence and language change protection
  const handleTabChange = (event, newValue) => {
    try {
      setTabValue(newValue);

      // Check if this is a legitimate user-initiated tab change
      // If switching to tab 0 during a language change, preserve the previous tab choice
      const currentSavedTab = LocalStorageService.getItem(`${type}_analyzer_tab_value`);

      if (newValue === 0 && currentSavedTab === '1') {
        // Check if this might be a language-change triggered reset
        // by checking if we're in the middle of a language change
        const isLanguageChanging = window.languageChangeInProgress || false;

        if (isLanguageChanging) {
          logger.info('[ANALYZER PAGE] Preventing tab reset during language change, keeping tab:', currentSavedTab);
          return; // Don't save the 0 value
        }
      }

      LocalStorageService.setItem(`${type}_analyzer_tab_value`, newValue.toString());
      logger.info('[ANALYZER PAGE] Tab changed to:', newValue);
    } catch (error) {
      logger.error('[ANALYZER PAGE] Error saving tab value to storage:', error);
      setTabValue(newValue); // Still set the state even if saving fails
    }
  };

  // Common render elements
  const renderStepper = () => (
    <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
      {steps.map((label, index) => (
        <Step key={label} completed={completedSteps.includes(index)}>
          <StepLabel>{label}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );

  const renderUploadStep = () => (
    <Box>
      <FileUpload
        onDrop={onDrop}
        files={files}
        text={text}
        onTextChange={handleTextChange}
        onRemoveFile={handleRemoveFile}
        accept={uploadProps?.accept}
        maxFiles={uploadProps?.maxFiles}
        maxSize={uploadProps?.maxSize}
        placeholder={uploadProps?.placeholder}
        textPlaceholder={uploadProps?.textPlaceholder}
        helperText={uploadProps?.helperText}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Box>
          {versions.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setTabValue(1)}
            >
              {t('common.versionHistory')}
            </Button>
          )}
        </Box>
        <Button
          variant="contained"
          onClick={onConfirm || handleConfirm}
          disabled={!hasValidInput() || analyzing}
        >
          {analyzing ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              {t('common.loading')}
            </>
          ) : (
            t('common.analyze')
          )}
        </Button>
      </Box>
    </Box>
  );

  const renderResultStep = () => (
    <Box>
      {loading ? (
        <LoadingState
          message={t('common.loading')}
          fullHeight
          component={`${type}AnalyzerPage`}
        />
      ) : result && renderDataDisplay ? (
        <>
          {viewingVersion && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {`Viewing Version ${viewingVersion.versionNumber}`}
            </Alert>
          )}

          {renderDataDisplay(
            result,
            customState,
            updateCustomState,
            setOpenEditDialog,
            setResult,
            handleRemoveSkill,
            _moveToNextStep,
            startEditing,
            stopEditing,
            handleSaveCompanyData
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}

          {error && (
            <ErrorState
              error={error}
              title={t('common.error')}
              context={`${type} analysis`}
              onRetry={() => {
                setError(null);
                logger.info(`${type}AnalyzerPage`, 'Error cleared by user');
              }}
              component={`${type}AnalyzerPage`}
            />
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={handleBack}>
              {t('common.back')}
            </Button>
            <Box>
              {type !== 'company' && (
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveItems}
                >
                  {t('common.save')}
                </Button>
              )}
            </Box>
          </Box>
        </>
      ) : (
        <EmptyState
          title={t('common.noDataFound')}
          message="Please upload a file to analyze"
          action={handleBack}
          actionText={t('common.back')}
          component={`${type}AnalyzerPage`}
        />
      )}
    </Box>
  );

  return (
    <Box key={renderKey}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom align="center">
          {title}
        </Typography>

        {!viewOnly && renderStepper()}

        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label={t('common.currentVersion')} />
          <Tab label={t('common.versionHistory')} />
        </Tabs>

        {tabValue === 0 && (
          activeStep === 0 ? renderUploadStep() : renderResultStep()
        )}

        {tabValue === 1 && (
          <VersionHistory
            versions={versions}
            onViewVersion={handleVersionSelect}
            onRestoreVersion={handleVersionRestore}
            onVersionCompare={(v1, v2) => logger.info('Compare versions:', v1, v2)}
            type={type}
            canEdit={true}
          />
        )}
      </Paper>

      {/* Edit Dialog (CV/Job): allow add-mode when editingIndex === -1 */}
      {openEditDialog && type !== 'company' && renderEditDialog && (editingItem || editingIndex === -1) && (
        renderEditDialog(editingItem, editingIndex, {
          open: openEditDialog,
          onClose: stopEditing,
          onSave: async (updatedItem) => {
            try {
              if (!handleSaveEdit) {
                throw new Error('Save handler is not configured for this analyzer');
              }

              const updatedResult = await handleSaveEdit(editingIndex, updatedItem, result, setResult);
              const resultToPersist = updatedResult || result;
              if (!resultToPersist) {
                throw new Error('No analysis result available to persist');
              }

              const itemLabel = type === 'cv' ? 'competency' : 'skill';
              const operation = editingIndex === -1 ? `add_${itemLabel}` : `edit_${itemLabel}`;
              await saveAnalysisResult(getTargetUserId(), resultToPersist, {
                timestamp: new Date().toISOString(),
                operation
              });

              setSuccess(t('common.saveSuccess'));
              stopEditing();
            } catch (saveError) {
              logger.error('[ANALYZER PAGE] Error saving dialog edit:', saveError);
              setError(saveError?.message || t('errors.genericError'));
            }
          }
        })
      )}

      {/* Company Research Edit Dialog */}
      {openEditDialog && type === 'company' && renderEditDialog && (
        renderEditDialog(
          openEditDialog,
          setOpenEditDialog,
          customState,
          updateCustomState,
          result,
          setResult,
          handleSaveCompanyData
        )
      )}

      {/* Back Confirmation Dialog */}
      <Dialog open={openBackConfirmDialog} onClose={() => setOpenBackConfirmDialog(false)}>
        <DialogTitle>{t('common.confirmBackTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('common.confirmBackMessage')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBackConfirmDialog(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleBackConfirm} color="primary">
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AnalyzerPage;
